import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
const USE_CASES = ['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other'];
export function WatchGigaDemo() {
  useScreenInit();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [size, setSize] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [workflows, setWorkflows] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your work email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/demo/book');
    }, 800);
  };
  return <div className="min-h-screen bg-[#1C1510] flex items-center justify-center py-24 px-6 relative overflow-hidden" data-id="element-13410" data-mp="3he5h0vcgyqck">
      {/* Background accents */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-13411" data-mp="3m66ps7afhyxu" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-13412" data-mp="3r2x34muumgfk" />

      <div className="relative z-10 w-full max-w-2xl" data-id="element-13413" data-mp="4vyxhzurzv2f">
        <AnimatePresence mode="wait" data-id="element-13414" data-mp="4udzu1d5zw0c">
          <motion.div key="form" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden" data-id="element-13415" data-mp="3bd7hu171apfi">
            <div className="p-8 md:p-10 border-b border-white/5" data-id="element-13416" data-mp="3gctrk0nnlnjk">
              <h1 className="font-title font-bold text-3xl text-white mb-3" data-id="element-13417" data-mp="3u4i3a2tzqa00">
                Meet Giga, your team's new coworker.
              </h1>
              <p className="text-neutral-400 leading-relaxed" data-id="element-13418" data-mp="3p4m1ma8rmunm">
                Tell us about your company and what you want to see, so we can
                prepare a personalized live demo. Mentioning specific tools
                helps Giga tailor the examples perfectly to your stack.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-8" data-id="element-13419" data-mp="29qramim25u5l">
              {/* Email */}
              <div className="space-y-3" data-id="element-13420" data-mp="24rmz3lbnvkc3">
                <label htmlFor="email" className="block text-sm font-bold text-white" data-id="element-13421" data-mp="k79i4miruwv4">
                  Your work email
                </label>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className={`w-full bg-black/20 border ${error ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#EA580C]'} rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-[#EA580C] transition-colors`} data-id="element-13422" data-mp="p5tab4nyl4vx" />
                {error && <p className="text-red-400 text-sm flex items-center gap-1.5 mt-2" data-id="element-13423" data-mp="5gaezr0ph0ix">
                    <AlertCircle className="w-4 h-4" data-id="element-13424" data-mp="3nqclomwlhsuz" />
                    {error}
                  </p>}
              </div>

              {/* Company Size */}
              <div className="space-y-3" data-id="element-13425" data-mp="1lxws7wks1um6">
                <label htmlFor="size" className="block text-sm font-bold text-white" data-id="element-13426" data-mp="1luseo8lf9miu">
                  How many people work at your company?
                </label>
                <div className="relative" data-id="element-13427" data-mp="3mxtbnrwvo2aj">
                  <select id="size" value={size} onChange={e => setSize(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-13428" data-mp="3mwqkrjzk1zum">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-13429" data-mp="391fsmecp3sty">
                      Select company size...
                    </option>
                    {COMPANY_SIZES.map(s => <option key={s} value={s} className="bg-[#2B211A]" data-id="element-13430" data-mp="2z87etb3z9z5p">
                        {s}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-13431" data-mp="2q9n1kph6shov">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-13432" data-mp="2l8fs8lzebag3">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-13433" data-mp="2gb65xf7azewh" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Use Cases */}
              <div className="space-y-3" data-id="element-13434" data-mp="15q9u8wpr73ay">
                <label htmlFor="usecase" className="block text-sm font-bold text-white" data-id="element-13435" data-mp="15rkvaortdwyd">
                  What would Giga help you with? Select from use cases below:
                </label>
                <div className="relative" data-id="element-13436" data-mp="d3p0jplt2f48">
                  <select id="usecase" value={selectedUseCase} onChange={e => setSelectedUseCase(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-13437" data-mp="i18gcty5izya">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-13438" data-mp="3qhjzqo9nhnw8">
                      Select a use case...
                    </option>
                    {USE_CASES.map(uc => <option key={uc} value={uc} className="bg-[#2B211A]" data-id="element-13439" data-mp="11o5bk8qpzx4o">
                        {uc}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-13440" data-mp="woix5vz5oaua">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-13441" data-mp="1bg1afm0mrr9y">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-13442" data-mp="ip9jp42c3uu0" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Specific Workflows */}
              <div className="space-y-3" data-id="element-13443" data-mp="rriqbiuhf424">
                <label htmlFor="workflows" className="block text-sm font-bold text-white" data-id="element-13444" data-mp="nom65npamzw2">
                  What specific workflows and tools should Giga handle?
                </label>
                <p className="text-xs text-neutral-400 mb-2" data-id="element-13445" data-mp="1z3j981ibbpmf">
                  List them and we'll prepare live examples for you.
                </p>
                <textarea id="workflows" value={workflows} onChange={e => setWorkflows(e.target.value)} placeholder="e.g. Pulling Stripe revenue into a weekly Slack report, or creating Linear issues from Zendesk tickets..." rows={4} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors resize-none" data-id="element-13446" data-mp="26m8zwbtcgvwe" />
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-white/5 flex justify-end" data-id="element-13447" data-mp="2uc3asnu3sae0">
                <button type="submit" disabled={isSubmitting} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-3.5 rounded-xl font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-13448" data-mp="2fmp11emm7qy3">
                  {isSubmitting ? <span className="flex items-center gap-2" data-id="element-13449" data-mp="3d3dzcyccwb2q">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-13450" data-mp="3d4yw8um5863r">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-13451" data-mp="189wcg4ovu51v"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-13452" data-mp="1492q3fr38yb3"></path>
                      </svg>
                      Preparing...
                    </span> : <>
                      Prepare my live demo
                      <ArrowRight className="w-4 h-4" data-id="element-13453" data-mp="1rwaxq4bzojga" />
                    </>}
                </button>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}